<script setup lang="ts">
import { getDtwinTypeLabel } from "../../dtwin/utils/getDtwinTypeLabel";
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { onMounted } from "vue";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import { useFetchDtwinModels } from "~/modules/dtwin/composables/useFetchDtwinModels";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import { PATH_DTWINS_ID } from "~/modules/dtwin/constants";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { router } from "~/plugins/router";
import { SectorResource } from "~/types";

const props = defineProps<{
  sector: Sector;
}>();

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const {
  isLoading: isLoadingDtwins,
  results: dtwins,
  error: errorDtwins,
  fetchData,
  getDescription,
  canManageItems,
} = useFetchDtwins({
  organizationCode: currentOrganization.value?.code,
  sectorCode: props.sector.code,
});

const {
  isLoading: isLoadingAllDtwinModels,
  error: errorAllDtwinModels,
  results: resultsAllDtwinModels,
  fetchAll: fetchAllDtwinModels,
} = useFetchDtwinModels({
  organizationCode: currentOrganization.value?.code,
});

const selectedItems: Ref<Dtwin[]> = ref([]);
const displayActions = computed(() => canManageItems(props.sector));

onMounted(async () => {
  await fetchAllDtwinModels();
  await fetchData();
});

async function refresh() {
  await fetchData();
  selectedItems.value = [];
}

function onRowClick(dtwin: Dtwin) {
  router.push({ name: PATH_DTWINS_ID, params: { id: dtwin.uid } });
}

const isLoading = computed(
  () => isLoadingAllDtwinModels.value || isLoadingDtwins.value,
);

async function getNextByPageAndSize(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  await fetchData(page, pageSize);
}
</script>

<template>
  <sector-toolbar
    ref="sectorToolbar"
    primary-key="uid"
    :selected-items="selectedItems"
    :sector="sector"
    :resource="SectorResource.DTWINS"
    @change="refresh"
  >
    <lxc-table-column
      prop="attributes.friendlyName"
      :label="t('dtwins.list.attributes.friendlyName')"
    />
    <lxc-table-column
      prop="attributes.type"
      :label="t('dtwins.list.attributes.type')"
      width="12rem"
    >
      <template v-if="resultsAllDtwinModels" #default="scope">
        {{ getDtwinTypeLabel(resultsAllDtwinModels, scope.row) }}
      </template>
    </lxc-table-column>
    <lxc-table-column :label="t('dtwins.list.attributes.state')" width="12rem">
      <template #default="scope">
        {{ t(`dtwins.lifeCycleState.${scope.row.lifeCycleState}`) }}
      </template>
    </lxc-table-column>
  </sector-toolbar>

  <container-component
    :px="0"
    :py="0"
    class="mt-4"
    :is-loading="isLoading && !dtwins"
    :error="errorAllDtwinModels || errorDtwins"
  >
    <sectors-details-table
      v-model="selectedItems"
      primary-key="uid"
      :is-loading="isLoading"
      :selection="displayActions"
      :items="dtwins"
      :display-actions="displayActions"
      :resource="SectorResource.DTWINS"
      :get-description="getDescription"
      @pagination:get-next-page="getNextByPageAndSize"
      @actions:detach="(item) => $refs.sectorToolbar.openDetachModal([item])"
      @row-click="onRowClick"
    >
      <lxc-table-column
        prop="attributes.friendlyName"
        :label="t('dtwins.list.attributes.friendlyName')"
        min-width="20rem"
      />
      <lxc-table-column
        prop="attributes.type"
        :label="t('dtwins.list.attributes.type')"
        width="12rem"
      >
        <template v-if="resultsAllDtwinModels" #default="scope">
          {{ getDtwinTypeLabel(resultsAllDtwinModels, scope.row) }}
        </template>
      </lxc-table-column>
      <lxc-table-column
        prop="attributes.serialNumber"
        :label="t('dtwins.list.attributes.serialNumber')"
        width="12rem"
      />
      <lxc-table-column
        :label="t('dtwins.list.attributes.state')"
        width="12rem"
      >
        <template #default="scope">
          {{ t(`dtwins.lifeCycleState.${scope.row.lifeCycleState}`) }}
        </template>
      </lxc-table-column>
    </sectors-details-table>
  </container-component>
</template>
